import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card } from '@progress/kendo-react-layout';
import cn from 'clsx';
import styles from './style.module.scss';
import { ScoreColor, ScoreDirection } from '@components-clean/central-monitoring/panels/panel-constants';
import { bankersRound } from '@components-clean/core/charts/shared/number-utils';
import Tooltip from '@components-clean/core/tooltip';
import { RAGWidgetProps } from '@components-clean/core/widgets/widget-interfaces';
import { useTranslation } from '@hooks/index';

const RagChangeWidgetItem = ({
    title,
    titleTooltip,
    ragScore,
    comparisonRagScore,
    isDraggable,
    onClose,
    isActiveItem,
    comparisonDatasetSelected,
    scoreColorInFocus,
    scoreColorComparison,
    changeDirection,
    'data-testid': testDataId,
}: RAGWidgetProps) => {
    const { t } = useTranslation('cm');

    let changeDirectionString = null;
    let inFocusStyleColor;
    let comparisonStyleColor = styles.ragScorePrimaryValue;

    switch (scoreColorInFocus) {
        case ScoreColor.Green:
            inFocusStyleColor = styles.ragScoreGreenValue;
            break;
        case ScoreColor.Amber:
            inFocusStyleColor = styles.ragScoreAmberValue;
            break;
        case ScoreColor.Red:
            inFocusStyleColor = styles.ragScoreRedValue;
            break;
        case ScoreColor.Purple:
            inFocusStyleColor = styles.ragScorePurpleValue;
            break;
        default:
            inFocusStyleColor = styles.ragScorePrimaryValue;
            break;
    }

    if (comparisonDatasetSelected) {
        changeDirectionString =
            changeDirection === null || changeDirection === undefined
                ? null
                : changeDirection === ScoreDirection.Matching
                  ? '-'
                  : changeDirection === ScoreDirection.Upper
                    ? 'asc'
                    : 'desc';

        if (comparisonRagScore) {
            switch (scoreColorComparison) {
                case ScoreColor.Green:
                    comparisonStyleColor = styles.ragScoreGreenValue;
                    break;
                case ScoreColor.Amber:
                    comparisonStyleColor = styles.ragScoreAmberValue;
                    break;
                case ScoreColor.Red:
                    comparisonStyleColor = styles.ragScoreRedValue;
                    break;
                case ScoreColor.Purple:
                    comparisonStyleColor = styles.ragScorePurpleValue;
                    break;
                default:
                    comparisonStyleColor = styles.ragScorePrimaryValue;
                    break;
            }
        }
    }

    return (
        <Tooltip position="top">
            <Card
                className={cn(
                    'widget k-border k-border-solid k-border-transparent',
                    styles.widgetCardStyling,
                    isActiveItem && styles.activeItem,
                )}
                data-testid={testDataId}
            >
                <div className={cn('k-d-flex k-align-content-center', styles.cardHeaderStyling)}>
                    <div className={cn(styles.cardTitleStyling)}>
                        {isDraggable && (
                            <FontAwesomeIcon
                                icon={['fas', 'grip-dots-vertical']}
                                className={cn('fa-lg', styles.dragIcon)}
                                style={{ margin: '0px' }}
                            />
                        )}
                        <div className={cn(styles.cardTitleTextStyling)} title={(titleTooltip || title) as string}>
                            {title}
                        </div>
                    </div>
                </div>
                <div className={cn(styles.widgetBody)}>
                    <div className={cn(styles.comparisonScorevalue, comparisonStyleColor)}>
                        <span
                            title={
                                !comparisonRagScore
                                    ? comparisonDatasetSelected
                                        ? t('componentsClean.widgets.noDataToCompare')
                                        : t('componentsClean.widgets.comparisonDatasetNotSelected')
                                    : undefined
                            }
                        >
                            {comparisonRagScore ? (
                                bankersRound(comparisonRagScore, 2)
                            ) : (
                                <FontAwesomeIcon
                                    icon={['far', 'hyphen']}
                                    className={cn('fa-lg', styles.dragIcon, comparisonStyleColor)}
                                    style={{ margin: '0px' }}
                                />
                            )}
                        </span>
                    </div>
                    <div className={cn(styles.ragScoreContainer)}>
                        <div className={cn(styles.borderBox)}>
                            <div className={cn(styles.ragScoreBox)}>
                                <div className={cn(styles.ragScoreValues)}>
                                    <span
                                        style={{ fontSize: '21px' }}
                                        title={!ragScore ? t('componentsClean.widgets.noDataToCompare') : undefined}
                                        className={cn(inFocusStyleColor)}
                                    >
                                        {ragScore ? bankersRound(ragScore, 2) : '--'}
                                    </span>
                                    <span style={{ marginLeft: 'auto', paddingRight: '10px' }}>
                                        <FontAwesomeIcon
                                            icon={
                                                changeDirectionString
                                                    ? changeDirectionString === '-'
                                                        ? ['far', 'hyphen']
                                                        : ['fass', 'triangle']
                                                    : ['far', 'hyphen']
                                            }
                                            className={cn({
                                                [styles.sortAscIcon]: changeDirectionString === 'asc',
                                                [`${styles.sortDescIcon} k-rotate-180`]:
                                                    changeDirectionString === 'desc',
                                            })}
                                        />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {isDraggable && (
                    <FontAwesomeIcon
                        icon={['far', 'close']}
                        className={cn(styles.closeIcon)}
                        onClick={(event) => {
                            event.stopPropagation();
                            onClose();
                        }}
                        data-testid={`${testDataId}-close`}
                    />
                )}
            </Card>
        </Tooltip>
    );
};

export default RagChangeWidgetItem;
